import { useState } from 'react';

import { useCentralizedProviderData, useMe } from '@crehana/auth';
import { CrossPlatformNavigation } from '@crehana/compass.layouts';
import { LayoutWithNavbarAndSidebar } from '@crehana/compass.layouts/layout-with-navbar-and-sidebar';
import { PageLoader } from '@crehana/compass.ui/v2';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { useTranslation } from '@crehana/i18n';
import { MainMenu, type MainMenuProps, useUITheme } from '@crehana/web';
import { useRouter } from 'next/router';

import { FEATURE_FLAGS_V5 } from '@/shared/components/AdmindashboardView/featureFlags';
import { useCentralizedOrganization } from '@/shared/hooks';
import { useConfiguration } from '@/views/AdminDashboard/hooks';

interface CrossPlatformLayoutProps {
  slug: string;
}

const CrossPlatformLayout: React.FC<
  React.PropsWithChildren<CrossPlatformLayoutProps>
> = ({ children, slug }) => {
  const { push } = useRouter();
  const { isDark } = useUITheme();
  const { countryCode } = useTranslation();
  const { me } = useMe();
  const { originalId: centralizedOrganizationId, isLoading } =
    useCentralizedOrganization();

  const { loading: loadingCentralized, currentPageOrganization } =
    useCentralizedProviderData();

  const { originalId } = useConfiguration();

  const { flagValue: isSidebarMenuEnabled, loading: loadingSidebarMenu } =
    useFeatureFlag(FEATURE_FLAGS_V5.NEW_SIDEBAR_LAYOUT);

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const defaultMainMenuProps: MainMenuProps = {
    layoutConfig: {
      hideCatalogDropdown: true,
      hideSearch: true,
      hideResourcesDropdown: true,
      hideB2BDashboardButtonLink: true,
      hideDiplomasLink: true,
      hideMentorDashboardLink: true,
      hideCategories: true,
      hideNewCourses: true,
      hideB2BAdminDashboardLink: false,
      hideUserDashboardLink: true,
      hideSwitchToOldHomeUserLink: false,
      hideInboxLink: true,
      hideMentorLandingLink: true,
      hideCrehanaCoinsButton: true,
      hideNotifications: true,
      embeddedInBreakpoints: ['sm', 'md', 'lg', 'xl'],
      leftLogoWrapperClassNames: 'hidden sm:flex',
      navType: 'dashboard',
      newSidebarEnabled: true,
      hideRightCatalog: true,
      hasProductsSwitch: false,
    },
    logoType: 'b2b',
    countryPrefix: countryCode!,
    amplitudePageName: 'AMPLITUDE_PAGE_NAME',
    embeddedIn: 'new-dashboard',
    theme: isDark ? 'dark' : 'light',
    currentUrl: '/org/[slug]/panel',
    customThemes: {
      light: {
        nav: {
          height: { xs: 56, sm: 62, md: 62, lg: 62 },
        },
      },
      dark: {
        nav: {
          height: { xs: 56, sm: 62, md: 62, lg: 62 },
        },
      },
    },
    onLeftMenuClick: () => {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    },
    organizationSlug: slug,
    isAdminPanel: true,
    isProduction: process.env.APP_ENV === 'production',
    onNavigateUrl: (url: string) => {
      push(url.replace('[slug]', slug));
    },
    hasIntegration: true,
    centralizeOrganizationId: centralizedOrganizationId,
  };

  if (isLoading || loadingSidebarMenu || loadingCentralized) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen bg-base-lighter-8">
        <PageLoader size="s" />
      </div>
    );
  }

  if (isSidebarMenuEnabled && currentPageOrganization) {
    return (
      <LayoutWithNavbarAndSidebar
        organizationSlug={currentPageOrganization.slug}
        centralizedOrganizationId={currentPageOrganization.id}
        viewMode="admin"
        handledPathnames={[]}
        onHandledUrlClick={push}
      >
        {children}
      </LayoutWithNavbarAndSidebar>
    );
  }

  return (
    <div className="tw-h-screen tw-flex tw-flex-col">
      <nav className="tw-border-solid tw-border-b-[1px] tw-border-b-neutral-light-300 dark:tw-border-b-elevation-dark-04 tw-bg-neutral-light-100 dark:tw-bg-elevation-dark-01">
        <MainMenu {...defaultMainMenuProps} />
      </nav>
      <div className="tw-flex tw-flex-grow">
        <CrossPlatformNavigation
          email={me?.email || ''}
          organizationSlug={slug}
          sidebarType="admin"
          mobileSidebarOpen={isMobileSidebarOpen}
          organizationId={originalId}
          handleUrl={(url: string) => {
            // all hris urls lives in another microfrontend
            if (url.includes('hris/')) {
              window.location.href = url;
              return;
            }

            push(url);
          }}
          hideMobileSidebar={() => {
            setIsMobileSidebarOpen(false);
          }}
          productPlatform="learning"
        />
        <main className="tw-overflow-y-auto	tw-bg-neutral-light-200 dark:tw-bg-elevation-dark-00 tw-text-neutral-light-800 dark:tw-text-neutral-light-400 tw-flex-grow tw-h-[calc(100vh-63px)]">
          {children}
        </main>
      </div>
    </div>
  );
};

export default CrossPlatformLayout;
